import { cloneDeep, uniqueId } from 'lodash'
import React, { createContext, MutableRefObject, ReactNode, useEffect, useRef, useState } from 'react'
import { useCreationCropType } from '../../hooks/use-creation-crop-type'
import { CreationToolSourceFile } from '../../types'
import CreationToolCrop from '../../types/crop'
import CreationToolError from '../../types/error'
import CreationToolFont from '../../types/font'
import CreationToolFontSize from '../../types/font-size'
import CreationToolOutputType from '../../types/output-type'
import CreationToolSourceEditor from '../../types/source-editor'
import { ArtboardRef } from '../artboard/types'
import { useEdits } from './hooks/use-edits'
import { useError } from './hooks/use-error'
import {
    SourceEditorCaption,
    SourceEditorCaptionStyle,
    SourceEditorCrop,
    SourceEditorEdits,
    SourceEditorTimeline,
    SourceEditorWatermark,
} from './types'
import { getDefaultCaptionPosition } from './utils'

type previewPlayheadTime = {
    current: number
}

export type CreationSourceEditorContextProps = {
    source: CreationToolSourceFile
    edits?: SourceEditorEdits
    currentEditor: CreationToolSourceEditor
    captions: SourceEditorCaption[]
    crop: SourceEditorCrop
    timeline: SourceEditorTimeline
    cropType: CreationToolCrop
    outputType: CreationToolOutputType
    artboardRef: MutableRefObject<ArtboardRef | null>
    customFontUrl?: string
    watermark?: SourceEditorWatermark
    previewPlayheadTime: previewPlayheadTime
    error?: CreationToolError
    forceRedraw: boolean
    setCurrentEditor: (editor: CreationToolSourceEditor) => void
    setCaptions: (captions: SourceEditorCaption[]) => void
    setCrop: (crop: SourceEditorCrop) => void
    setTimeline: (timeline: SourceEditorTimeline) => void
    setCropType: (cropType: CreationToolCrop) => void
    setPreviewPlayheadTime: (playheadTime: number) => void
    duplicate: (caption: SourceEditorCaption) => void
    flip: (caption: SourceEditorCaption) => void
    sendToBack: (caption: SourceEditorCaption) => void
    toggleCaptionLayerOptions: (caption: SourceEditorCaption) => void
    toggleCaptionStyleOptions: (caption: SourceEditorCaption) => void
    setForceRedraw: (value: boolean) => void
    deleteCaption: (caption: SourceEditorCaption) => void
}

export const CreationSourceEditorContext = createContext({} as CreationSourceEditorContextProps)

type Props = {
    defaultEditor?: CreationToolSourceEditor
    defaultEdits?: SourceEditorEdits
    customFontUrl?: string
    watermark?: SourceEditorWatermark
    source: CreationToolSourceFile
    outputType: CreationToolOutputType
    children: ReactNode
    onUpdate?: (edits?: SourceEditorEdits) => void
}

const CreationSourceEditorContextManager = ({
    defaultEditor = CreationToolSourceEditor.Edit,
    customFontUrl,
    watermark,
    defaultEdits,
    outputType,
    source,
    children,
    onUpdate,
}: Props) => {
    const defaultCropType = useCreationCropType(
        defaultEdits?.crop?.width || source.width,
        defaultEdits?.crop?.height || source.height,
        source.width / source.height
    )
    const [currentEditor, setCurrentEditor] = useState<CreationToolSourceEditor>(defaultEditor)
    const [cropType, setCropType] = useState<CreationToolCrop>(defaultCropType)
    const [captions, setCaptions] = useState<SourceEditorCaption[]>(defaultEdits?.captions || [])
    const [forceRedraw, setForceRedraw] = useState<boolean>(false)
    const artboardRef = useRef<ArtboardRef | null>(null)
    const previewPlayheadTime = useRef<number>(0)

    const setPreviewPlayheadTime = (number: number) => {
        previewPlayheadTime.current = number
    }

    const [timeline, setTimeline] = useState<SourceEditorTimeline>(
        defaultEdits?.trim || {
            start: 0,
            end: source.duration,
        }
    )

    const [crop, setCrop] = useState<SourceEditorCrop>(
        defaultEdits?.crop || {
            x: 0,
            y: 0,
            width: source.width,
            height: source.height,
        }
    )

    const edits = useEdits({ source, timeline, captions, crop })
    const error = useError(outputType, edits)

    const duplicate = (caption: SourceEditorCaption) => {
        const newCaption = cloneDeep(caption)
        newCaption.id = uniqueId('caption')
        newCaption.options.isLayerOptionsOpened = false
        newCaption.options.isStyleOptionsOpened = false

        const newCaptions = [...captions]

        // Check if the last caption is not filled
        if (newCaptions[newCaptions.length - 1].text.length === 0) {
            // If not, replace with the duplicate caption
            newCaptions[newCaptions.length - 1] = newCaption
        } else {
            // If has text, create a new index
            newCaptions.push(newCaption)
        }

        setCaptions(newCaptions)
    }

    const deleteCaption = (caption: SourceEditorCaption) => {
        const index = captions.findIndex(c => caption.id === c.id)
        const newCaptions = [...captions]
        newCaptions.splice(index, 1)
        setCaptions(newCaptions)
    }

    const flip = (caption: SourceEditorCaption) => {
        const newCaption = { ...caption }
        newCaption.style.mirror = !newCaption.style.mirror
        const newCaptions = captions.map(c => {
            if (caption.id === c.id) {
                return { ...newCaption }
            }

            return c
        })

        setForceRedraw(true)
        setCaptions(newCaptions)
    }

    const sendToBack = (caption: SourceEditorCaption) => {
        const newCaptions = captions.filter((c, index) => !(c.text.length === 0 && index === captions.length - 1))

        const index = newCaptions.findIndex(c => {
            return caption.id === c.id
        })

        newCaptions.splice(index, 1).pop()
        newCaptions.push({ ...caption })
        setForceRedraw(true)
        setCaptions(newCaptions)
    }

    const toggleCaptionLayerOptions = caption => {
        setCaptions(
            captions.map(c => {
                if (caption.id === c.id) {
                    c.options.isLayerOptionsOpened = !caption.options.isLayerOptionsOpened
                } else {
                    c.options.isLayerOptionsOpened = false
                }

                return c
            })
        )
    }

    const toggleCaptionStyleOptions = caption => {
        setCaptions(
            captions.map(c => {
                if (caption.id === c.id) {
                    c.options.isStyleOptionsOpened = !caption.options.isStyleOptionsOpened
                } else {
                    c.options.isStyleOptionsOpened = false
                }

                return c
            })
        )
    }

    const generateNewCaption = () => {
        const { x, y, maxWidth } = getDefaultCaptionPosition(source, edits)

        let style: SourceEditorCaptionStyle = {
            color: '#fff',
            font: customFontUrl ? CreationToolFont.Custom : CreationToolFont.Giphy,
            fontSize: CreationToolFontSize.Medium,
            mirror: false,
        }

        if (captions.length > 0) {
            const lastCaption = captions[captions.length - 1]
            style = lastCaption.style
        }

        setCaptions([
            ...captions,
            {
                id: uniqueId('caption'),
                text: '',
                style: {
                    ...style,
                },
                x,
                y,
                maxWidth,
                rotation: 0,
                options: {},
            },
        ])
    }

    const openCaptionStyleOption = () => {
        if (captions.length === 1 && !captions[0].options.isStyleOptionsOpened) {
            captions[0].options.isStyleOptionsOpened = true
            setCaptions([...captions])
        }
    }

    useEffect(() => {
        // Check if there is no empty inputs, if dont, add new one
        if (captions.filter(caption => caption.text.length === 0).length === 0) {
            generateNewCaption()
            return
        }

        // If there is only one caption, open it
        if (captions.length === 1) {
            openCaptionStyleOption()
        }
    }, [captions])

    useEffect(() => {
        onUpdate && onUpdate(edits)
    }, [edits, onUpdate])

    return (
        <CreationSourceEditorContext.Provider
            value={{
                source,
                currentEditor,
                captions,
                crop,
                timeline,
                outputType,
                cropType,
                artboardRef,
                customFontUrl,
                watermark,
                edits,
                previewPlayheadTime,
                error,
                forceRedraw,
                setCurrentEditor,
                setCaptions,
                setCrop,
                setTimeline,
                setCropType,
                setPreviewPlayheadTime,
                duplicate,
                flip,
                sendToBack,
                deleteCaption,
                toggleCaptionLayerOptions,
                toggleCaptionStyleOptions,
                setForceRedraw,
            }}
        >
            {children}
        </CreationSourceEditorContext.Provider>
    )
}

export default CreationSourceEditorContextManager
